import { InputCheckbox } from '@creditornot/cb-checkbox';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React from 'react';
import { Field, FieldInputProps } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Typography } from 'common/typography';
import { useAdCampaignTranslationKeys } from 'modules/campaigns/ad-campaign/hooks/useAdCampaignTranslationKeys';
import { reportEvent } from 'modules/campaigns/behavioral';
import { TERMS_AND_CONDITIONS_LINK, TERMS_AND_CONDITIONS_LINK_SUMMER_CONTEST } from 'modules/campaigns/constants';
import { AdCampaignFormValues } from 'modules/campaigns/types';
import { shakingAnimationMixin } from 'modules/campaigns/utils/shakingCssAnimation';
import { TestIds } from 'modules/campaigns/utils/testUtils';

const StyledAgreementsWrapper = styled.div`
  display: flex;
  border-radius: 8px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding: 16px 0;
`;

const StyledCheckboxLabel = styled.label<{ isShaking?: boolean }>`
  display: flex;
  gap: 8px;
  color: ${cbSemanticCssVars.textPrimary};
  ${({ isShaking }) => (isShaking ? shakingAnimationMixin : 'animation: none')}
`;

const handleAcceptTermsAndConditionsClick =
  (input: FieldInputProps<boolean, HTMLElement>) => (e: React.FormEvent<HTMLInputElement>) => {
    input.onChange(e);
    if (e.currentTarget.checked) {
      reportEvent('NEW_AD_CAMPAIGN_TC_ACCEPTANCE');
    }
  };

type Props = {
  showErrorAnimation: boolean;
};

export const ExistingCampaignAgreement = ({ showErrorAnimation }: Props) => {
  const translationKeysPerCountry = useAdCampaignTranslationKeys();

  return (
    <StyledAgreementsWrapper data-test-id={TestIds.FORM.AGREEMENTS.WRAPPER}>
      <Field<AdCampaignFormValues['agreementTermsAndConditions']> name="agreementTermsAndConditions" type="checkbox">
        {({ input }) => (
          <StyledCheckboxLabel isShaking={showErrorAnimation && !input.checked}>
            <InputCheckbox
              name={input.name}
              checked={input.checked}
              onChange={handleAcceptTermsAndConditionsClick(input)}
            />
            <Typography.Body3>
              <FormattedMessage
                id={translationKeysPerCountry.agreementStatement}
                values={{
                  link: (
                    <a href={TERMS_AND_CONDITIONS_LINK} target="_blank" rel="noreferrer">
                      <FormattedMessage id="campaigns.agreements.terms-and-conditions-link" />
                    </a>
                  ),
                  summerContestLink: (
                    <a href={TERMS_AND_CONDITIONS_LINK_SUMMER_CONTEST} target="_blank" rel="noreferrer">
                      <FormattedMessage id="campaigns.agreements.terms-and-conditions-link-summer-contest" />
                    </a>
                  ),
                }}
              />
            </Typography.Body3>
          </StyledCheckboxLabel>
        )}
      </Field>
    </StyledAgreementsWrapper>
  );
};
